import {h, Fragment} from "preact"
import { useAppStateSelector, useSort, pick, useProductFilters } from "@nosto/preact"
import { useState, useEffect } from "preact/hooks"
import { sortOptions } from "../config"
import { Icon, Select } from "./elements"
import { toggleButtonId } from "./Sidebar"

const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 1024

function updateGridBorders(cols) {
    const grid = document.querySelector('.product-grid.new-layout');
    if (!grid || !cols) return;

    const items = grid.querySelectorAll('.grid__item');

    items.forEach(item => item.classList.remove('hide-border-right'));

    let visualCol = 0;

    items.forEach(item => {
        const isBanner = item.classList.contains('marketing-banner-item');
        const isLandscape = item.classList.contains('landscape');
        const span = (isBanner && isLandscape) ? 2 : 1;

        if (visualCol + span > cols) {
            visualCol = 0;
        }

        if (visualCol + span === cols) {
            item.classList.add('hide-border-right');
        }

        if (isBanner) {
            item.classList.add('hide-border-right');
        }

        visualCol += span;
        if (visualCol >= cols) visualCol = 0;
    });
}

function ToolbarActiveFilters({ filters, removeAll }) {
    if (!filters?.length) return null

    return (
        <div class="toolbar-active-filters ns-d-flex ns-flex-wrap ns-align-items-center">
            {filters.map(filter => {
                let displayValue = filter.value
                if (typeof displayValue === "string" && /[[\]"]/.test(displayValue)) {
                    displayValue = displayValue.replace(/[[\]"]/g, "")
                }

                return (
                    <button
                        key={`${filter.name}: ${filter.value}`}
                        type="button"
                        class="toolbar-active-filters__pill button button--tertiary font-secondary mono-sm"
                        onClick={() => filter.remove()}
                    >
                        <span>{displayValue}</span>
                        <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M10.5859 12L2.79297 4.20706L4.20718 2.79285L12.0001 10.5857L19.793 2.79285L21.2072 4.20706L13.4143 12L21.2072 19.7928L19.793 21.2071L12.0001 13.4142L4.20718 21.2071L2.79297 19.7928L10.5859 12Z" fill="currentColor"></path>
                        </svg>
                        <span class="visually-hidden">Remove filter</span>
                    </button>
                )
            })}
            <button
                type="button"
                class="toolbar-active-filters__clear underlined-link font-primary buttons-sm"
                onClick={() => removeAll()}
            >
                Clear All
            </button>
        </div>
    )
}

export default function ActionToolbar() {
    const { loading, query, response, pageType } = useAppStateSelector(state =>
        pick(state, "loading", "query", "response", "pageType")
    )
    
    const { filters: selectedFilters, removeAll } = useProductFilters()

    const [displayCount, setDisplayCount] = useState(selectedFilters?.length || 0);

    useEffect(() => {
        const newCount = selectedFilters?.length || 0;
        setDisplayCount(newCount);
        console.log("Display count updated to:", newCount, "Filters:", selectedFilters);
    }, [selectedFilters]);

    const selectedCount = displayCount;

    const [isNewLayout, setIsNewLayout] = useState(false)
    const [gridColumns, setGridColumns] = useState(() => isDesktop() ? 3 : 2)

    useEffect(() => {
        if (typeof window !== 'undefined' && window.isNewLayout) {
            setIsNewLayout(true)
        }

        setGridColumns(isDesktop() ? 3 : 2)

        window.dispatchEvent(new CustomEvent('gridColumnsChange', { 
            detail: { columns: isDesktop() ? 3 : 2 } 
        }))
    }, [])

    // Update grid borders only for 4-col when banner exists
    useEffect(() => {
        if (!isNewLayout) return;
        if (gridColumns !== 4) return;

        const grid = document.querySelector('.product-grid.new-layout');
        if (!grid) return;

        const hasBanner = grid.querySelector('.marketing-banner-item');
        if (!hasBanner) return;

        requestAnimationFrame(() => updateGridBorders(gridColumns));

        const observer = new MutationObserver(() => {
            requestAnimationFrame(() => updateGridBorders(gridColumns));
        });

        observer.observe(grid, { childList: true, subtree: false });

        return () => observer.disconnect();
    }, [gridColumns, isNewLayout, response]);

    const handleGridChange = (columns) => {
        setGridColumns(columns)
        window.dispatchEvent(new CustomEvent('gridColumnsChange', { detail: { columns } }))
    }

    const docCount = response.products?.total ?? 0
    
    const totalSummary = isNewLayout
    ? `${docCount} items`
    : `${docCount} results`;

    const { activeSort, setSort } = useSort(sortOptions)

    const handleSortChange = event => {
        setSort(event.target.value)
    }

    const options = sortOptions.map(o => ({ value: o.id, label: o.value.name }))

    return (
        <>
            <div id="searchspring-toolbar-header" class="facets facets-vertical-sort small-hide medium-hide">
                <div class="facets-vertical-form page-width" id="FacetSortForm">
                    <label id="HideFilters" for={toggleButtonId} class="font-primary heading-2-md">
                        <Icon name="sliders" />
                        Hide Filter
                    </label>
                    <div class="facet-filters sorting">
                        <div class="product-count-vertical" role="status">
                            <h2 class="product-count__text">
                                <span id="ProductCountDesktop" class="font-secondary mono-md">
                                    {totalSummary}
                                </span>
                            </h2>
                        </div>

                        <div class="facet-filters__field">
                            <div class="select">
                                <Icon name="sort-big" />
                                <label for="SortBy" class="facet-filters__label font-primary heading-2-md">
                                    Sort
                                </label>
                                <Select
                                    value={activeSort}
                                    onChange={e => handleSortChange(e)}
                                    options={options}
                                    label={"Sort by"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="facets-container ss__facets__container__mobile" data-breakpoint="mobile">
                <div class="product-count light large-up-hide tablet-hide" role="status">
                    <h2 class="product-count__text font-secondary mono-md">
                        <span id="ProductCount">{totalSummary}</span>
                    </h2>
                </div>
                <div class={`mobile-facets__wrapper ${isNewLayout ? '' : 'large-up-hide'} ${selectedCount > 0 ? 'has-filter' : ''}`} data-breakpoint="mobile">
                    <div class="mobile-facets__disclosure disclosure-has-popup">
                        <label for={toggleButtonId} class="mobile-facets__open-wrapper focus-offset">
                            <span role="button" class="mobile-facets__open-button font-primary heading-2-md">
                                <Icon name="sliders" />
                                {isNewLayout 
                                    ? "Filter & Sort"
                                    : "Filter & Sort"
                                }
                            </span>
                        </label>
                        {isNewLayout && (
                            <div class="selected-filter desktop small-hide small-tablet-hide">
                                <ToolbarActiveFilters filters={selectedFilters} removeAll={removeAll} />
                            </div>
                        )}
                    </div>
                    
                     {/* Grid Toggle - Desktop: 2 col & 3 col & 4 col */}
                    {isNewLayout && (
                        <div class={`toggle-wrapper ${selectedCount > 0 ? 'has-filter' : ''}`}>
                            <div class="product-count-vertical small-hide" role="status">
                                <h2 class="product-count__text">
                                    <span id="ProductCountDesktop" class="font-secondary mono-md">
                                        {totalSummary}
                                    </span>
                                </h2>
                            </div>
                            <div class="grid-toggle small-hide small-tablet-hide">
                                <button
                                    type="button"
                                    class={`grid-toggle__btn ${gridColumns === 2 ? 'is-active' : ''}`}
                                    onClick={() => handleGridChange(2)}
                                    aria-label="2 column view"
                                    aria-pressed={gridColumns === 2}
                                >
                                    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect y="12" width="12" height="12" transform="rotate(-90 0 12)" fill="currentColor"/>
                                    <rect x="13" y="12" width="12" height="12" transform="rotate(-90 13 12)" fill="currentColor"/>
                                    <rect y="25" width="12" height="12" transform="rotate(-90 0 25)" fill="currentColor"/>
                                    <rect x="13" y="25" width="12" height="12" transform="rotate(-90 13 25)" fill="currentColor"/>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    class={`grid-toggle__btn ${gridColumns === 3 ? 'is-active' : ''}`}
                                    onClick={() => handleGridChange(3)}
                                    aria-label="3 column view"
                                    aria-pressed={gridColumns === 3}
                                >
                                    <svg width="38" height="25" viewBox="0 0 38 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect y="12" width="12" height="12" transform="rotate(-90 0 12)" fill="currentColor"/>
                                    <rect x="13" y="12" width="12" height="12" transform="rotate(-90 13 12)" fill="currentColor"/>
                                    <rect x="26" y="12" width="12" height="12" transform="rotate(-90 26 12)" fill="currentColor"/>
                                    <rect y="25" width="12" height="12" transform="rotate(-90 0 25)" fill="currentColor"/>
                                    <rect x="13" y="25" width="12" height="12" transform="rotate(-90 13 25)" fill="currentColor"/>
                                    <rect x="26" y="25" width="12" height="12" transform="rotate(-90 26 25)" fill="currentColor"/>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    class={`grid-toggle__btn ${gridColumns === 4 ? 'is-active' : ''}`}
                                    onClick={() => handleGridChange(4)}
                                    aria-label="4 column view"
                                    aria-pressed={gridColumns === 4}
                                >
                                    <svg width="51" height="25" viewBox="0 0 51 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect y="12" width="12" height="12" transform="rotate(-90 0 12)" fill="currentColor"/>
                                        <rect x="13" y="12" width="12" height="12" transform="rotate(-90 13 12)" fill="currentColor"/>
                                        <rect x="26" y="12" width="12" height="12" transform="rotate(-90 26 12)" fill="currentColor"/>
                                        <rect x="39" y="12" width="12" height="12" transform="rotate(-90 39 12)" fill="currentColor"/>
                                        <rect y="25" width="12" height="12" transform="rotate(-90 0 25)" fill="currentColor"/>
                                        <rect x="13" y="25" width="12" height="12" transform="rotate(-90 13 25)" fill="currentColor"/>
                                        <rect x="26" y="25" width="12" height="12" transform="rotate(-90 26 25)" fill="currentColor"/>
                                        <rect x="39" y="25" width="12" height="12" transform="rotate(-90 39 25)" fill="currentColor"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Grid Toggle - Mobile: 1 col & 2 col */}
                    {isNewLayout && (
                        <div class="grid-toggle grid-toggle--mobile tablet-hide large-up-hide">
                            <button
                                type="button"
                                class={`grid-toggle__btn ${gridColumns === 1 ? 'is-active' : ''}`}
                                onClick={() => handleGridChange(1)}
                                aria-label="1 column view"
                                aria-pressed={gridColumns === 1}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="16" height="7" fill="currentColor"/>
                                    <rect y="9" width="16" height="7" fill="currentColor"/>
                                </svg>
                            </button>
                            <button
                                type="button"
                                class={`grid-toggle__btn ${gridColumns === 2 ? 'is-active' : ''}`}
                                onClick={() => handleGridChange(2)}
                                aria-label="2 column view"
                                aria-pressed={gridColumns === 2}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="7" height="7" fill="currentColor"/>
                                    <rect x="9" width="7" height="7" fill="currentColor"/>
                                    <rect y="9" width="7" height="7" fill="currentColor"/>
                                    <rect x="9" y="9" width="7" height="7" fill="currentColor"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
                {isNewLayout && (
                    <div class="selected-filer mobile tablet-hide large-up-hide">
                        <ToolbarActiveFilters filters={selectedFilters} removeAll={removeAll} />
                    </div>
                )}
            </div>
        </>
    )
}